import { useReducer } from 'react';

/**
 * _React Custom Hook_
 *
 * Can be used in two ways:
 *
 * ### 1. Rerender the entire component in which the hook is called
 * Using the basic form, simply initiate a render cycle in the component in which this hook and
 * returned function are called.
 *
 * ### 2. Rerender a particular child component through `key` equality
 * If you want to cause a particular child component to remount, but have no other value to pass as
 * the `key` prop of that component to cause the component to remount, you can pass the state value
 * returned as the first element of the array returned from this hook as that child component's
 * `key` prop. This value is stringified, and React will "see" that as a different value, causing
 * the child component to be remounted. **NOTE:** this 2nd form will **also** trigger a render cycle
 * of the component in which this hook is called.
 *
 * ## Examples
 * See [test file]{@link file://./../../../tests/lib/Hooks/useRerender.test.js} for examples
 *
 * @returns {[string, () => void]} An array with the key to pass to a child component that you wish
 * to programmatically rerender, and a function to initiate the rerender.
 */
export function useRerender() {
  // reducer used for triggering a re-render. the state isn't needed and is simply a counter in order
  // to return a "new" state that causes the re-render.
  const [state, rerender] = useReducer((x) => x + 1, 0);

  return [String(state), rerender];
}
